import { useI18n } from "@core/i18n/i18n";
import {
	getLanguageSelfNameFromIntlLocale,
	getLowercaseLanguageCodeFromIntlLocale,
	localeToIntlLocale,
} from "@core/i18n/locale-helper";

import { isNil } from "@utils/assert";
import { useMemo } from "react";
import { useI18nIsoCountries } from "@core/i18n-iso-countries/useI18nIsoCountries";
import { orderList } from "@utils/string-utils";

export type CountryLanguageOptions = Map<string, string>;

export const useCountryLanguageOptions = (
	locales: Map<string, string[]>,
	forceLongName: boolean,
	fallback: (countryCode: string) => string,
): CountryLanguageOptions => {
	const { format } = useI18n();
	const { getCountryName } = useI18nIsoCountries();

	return useMemo(() => {
		const options: { locale: string; label: string }[] = [];

		locales.forEach((countryLocales, countryCode) => {
			if (!forceLongName && countryLocales.length === 1) {
				const intlLocale = localeToIntlLocale(countryLocales[0]);
				const language = getLanguageSelfNameFromIntlLocale(intlLocale);
				const countryName = getCountryName(countryCode, language, fallback);

				options.push({
					locale: intlLocale,
					label: countryName,
				});
			}
			else {
				countryLocales.forEach(locale => {
					const intlLocale = localeToIntlLocale(locale);
					const languageCode = getLowercaseLanguageCodeFromIntlLocale(intlLocale);

					if (isNil(languageCode)) return;

					const language = getLanguageSelfNameFromIntlLocale(intlLocale);
					const countryName = getCountryName(countryCode, languageCode, fallback);

					options.push({
						locale: intlLocale,
						label: format("register.countrySelection.countryDropdown.CountryAndLanguage", {
							country: countryName,
							language,
						}),
					});
				});
			}
		});

		return new Map<string, string>(orderList(options, "label").map(({ locale, label }) => [locale, label]));
	}, [fallback, format, getCountryName, locales, forceLongName]);
};
